import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { Ambulance, CheckCircle2 } from "lucide-react";

const MIN_LEN = 10;

export default function ResetPassword() {
  const { token } = useParams();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < MIN_LEN) return setErr(`Password must be at least ${MIN_LEN} characters.`);
    if (!/[A-Za-z]/.test(pw) || !/[0-9]/.test(pw)) return setErr("Password must include at least one letter and one number.");
    if (pw !== confirm) return setErr("The two passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const input = "mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300";
  const label = "text-xs font-semibold uppercase tracking-[0.1em] text-slate-500";

  return (
    <div className="min-h-screen bg-[#F4F6F8] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-lg border border-slate-200 p-8" data-testid="reset-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-md bg-[#0A192F] flex items-center justify-center">
            <Ambulance className="text-white" size={20} />
          </div>
          <div>
            <div className="font-display font-semibold text-lg">{BRAND}</div>
            <div className="text-xs text-slate-500">Choose a new password</div>
          </div>
        </div>

        {done ? (
          <div data-testid="reset-done">
            <div className="flex items-center gap-2 text-emerald-700 font-medium"><CheckCircle2 size={18} /> Password updated</div>
            <p className="text-sm text-slate-600 mt-2">You can sign in with your new password now.</p>
            <Link to="/login" className="inline-block mt-6 btn-primary" data-testid="reset-to-login">Go to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <div>
              <label className={label}>New password</label>
              <input data-testid="reset-password" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} className={input} required />
              <div className="text-xs text-slate-500 mt-1">At least {MIN_LEN} characters, with a letter and a number.</div>
            </div>
            <div>
              <label className={label}>Confirm new password</label>
              <input data-testid="reset-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={input} required />
            </div>
            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="reset-error">{err}</div>}
            <button type="submit" disabled={busy} className="w-full btn-primary disabled:opacity-60" data-testid="reset-submit">
              {busy ? "Saving..." : "Set new password"}
            </button>
            <div className="text-sm text-slate-500">
              Link expired? <Link to="/forgot" className="text-[#005E82] font-medium hover:underline">Request a new one</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
